import React, { useState } from 'react';
import { Filter, X, Eye, EyeOff } from 'lucide-react';
import { colors } from '../styles/colors';

/**
 * Composant de barre de filtres réutilisable
 *
 * @param {Array} filters - Tableau de définitions de filtres
 * @param {ReactNode} infoDisplay - Contenu optionnel à afficher (ex: nombre de consultants)
 * @param {ReactNode} actionButtons - Boutons d'action optionnels
 * @param {ReactNode} resultCount - Affichage du nombre de résultats
 * @param {string} marginBottom - Marge en bas (par défaut '1.5rem')
 *
 * Exemple de filter:
 * {
 *   type: 'select' | 'multiselect',
 *   label: 'Année',
 *   value: filterAnnee,
 *   onChange: setFilterAnnee,
 *   options: [{ value: 2024, label: '2024' }, ...],
 *   placeholder: 'Toutes les années',
 *   minWidth: '180px'
 * }
 *
 * Pour multiselect:
 * {
 *   type: 'multiselect',
 *   label: 'Mois',
 *   value: [1, 3, 5], // Array de valeurs sélectionnées
 *   onChange: setFilterMois,
 *   options: [{ value: 1, label: 'Janvier' }, ...],
 *   placeholder: 'Tous les mois',
 *   formatDisplay: (values, options) => '3 mois', // Optionnel
 *   minWidth: '180px'
 * }
 *
 * Pour toggle:
 * {
 *   type: 'toggle',
 *   value: hidePaid,
 *   onChange: setHidePaid,
 *   labelOn: 'Payées masquées',
 *   labelOff: 'Masquer payées',
 *   iconOn: EyeOff,
 *   iconOff: Eye
 * }
 */
function FilterBar({
  filters = [],
  infoDisplay = null,
  actionButtons = null,
  resultCount = null,
  marginBottom = '1.5rem'
}) {
  // État pour les dropdowns multiselect ouverts
  const [openDropdowns, setOpenDropdowns] = useState({});

  const toggleDropdown = (index) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const closeDropdown = (index) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [index]: false
    }));
  };

  const renderFilter = (filter, index) => {
    const key = `filter-${index}`;

    // Simple select dropdown
    if (filter.type === 'select') {
      return (
        <select
          key={key}
          value={filter.value ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            // Si l'option a une valeur numérique, la convertir
            if (value && !isNaN(value)) {
              filter.onChange(Number(value));
            } else if (value === '') {
              filter.onChange(filter.allowNull ? null : '');
            } else {
              filter.onChange(value);
            }
          }}
          style={{
            padding: '0.5rem 2.5rem 0.5rem 0.75rem',
            border: `2px solid ${colors.secondary.lightTeal}`,
            borderRadius: '0.5rem',
            fontSize: '0.9375rem',
            fontFamily: '"Noto Sans", sans-serif',
            color: colors.primary.gray,
            backgroundColor: colors.text.white,
            cursor: 'pointer',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23007481' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0.75rem center',
            minWidth: filter.minWidth || 'auto'
          }}
          onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
          onBlur={(e) => e.target.style.borderColor = colors.secondary.lightTeal}
        >
          {filter.placeholder && <option value="">{filter.placeholder}</option>}
          {filter.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    // Multi-select dropdown
    if (filter.type === 'multiselect') {
      const isOpen = openDropdowns[index];
      const selectedValues = Array.isArray(filter.value) ? filter.value : [];
      const hasSelection = selectedValues.length > 0;

      // Fonction pour formater l'affichage du bouton
      const getDisplayText = () => {
        if (filter.formatDisplay) {
          return filter.formatDisplay(selectedValues, filter.options);
        }
        if (selectedValues.length === 0) {
          return filter.placeholder || 'Tous';
        }
        if (selectedValues.length === 1) {
          const selected = filter.options.find(opt => opt.value === selectedValues[0]);
          return selected ? selected.label : `1 élément`;
        }
        return `${selectedValues.length} éléments`;
      };

      return (
        <div key={key} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => toggleDropdown(index)}
            style={{
              padding: '0.5rem 2.5rem 0.5rem 0.75rem',
              border: `2px solid ${isOpen || hasSelection ? colors.primary.teal : colors.secondary.lightTeal}`,
              borderRadius: '0.5rem',
              fontSize: '0.9375rem',
              fontFamily: '"Noto Sans", sans-serif',
              color: colors.primary.gray,
              backgroundColor: colors.text.white,
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23007481' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              textAlign: 'left',
              minWidth: filter.minWidth || '180px'
            }}
          >
            {getDisplayText()}
          </button>

          {/* Bouton clear */}
          {hasSelection && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                filter.onChange([]);
              }}
              title="Effacer le filtre"
              style={{
                position: 'absolute',
                right: '2.25rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                padding: '0.25rem',
                cursor: 'pointer',
                color: colors.secondary.mediumGray,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={14} />
            </button>
          )}

          {/* Dropdown menu */}
          {isOpen && (
            <>
              <div
                onClick={() => closeDropdown(index)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 998
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '0.25rem',
                  backgroundColor: colors.text.white,
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  border: `1px solid ${colors.secondary.lightGray}`,
                  zIndex: 999,
                  minWidth: filter.minWidth || '200px',
                  padding: '0.5rem',
                  maxHeight: filter.maxHeight || '300px',
                  overflowY: 'auto'
                }}
              >
                {filter.options.map((opt) => {
                  const isChecked = selectedValues.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                        transition: 'background-color 150ms'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightGray}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          const newValues = isChecked
                            ? selectedValues.filter(v => v !== opt.value)
                            : [...selectedValues, opt.value];
                          filter.onChange(newValues);
                        }}
                        style={{
                          width: '1rem',
                          height: '1rem',
                          accentColor: colors.primary.teal
                        }}
                      />
                      <span style={{
                        fontSize: '0.875rem',
                        color: colors.primary.gray,
                        textTransform: filter.capitalizeLabels ? 'capitalize' : 'none'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
      );
    }

    // Toggle button
    if (filter.type === 'toggle') {
      const isActive = filter.value;
      const IconOn = filter.iconOn || EyeOff;
      const IconOff = filter.iconOff || Eye;
      const Icon = isActive ? IconOn : IconOff;
      const label = isActive ? filter.labelOn : filter.labelOff;

      return (
        <button
          key={key}
          onClick={() => filter.onChange(!filter.value)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.4rem 0.75rem',
            borderRadius: '0.375rem',
            border: `1px solid ${isActive ? colors.primary.teal : colors.secondary.lightTeal}`,
            backgroundColor: isActive ? colors.secondary.lightTeal : 'transparent',
            fontSize: '0.8125rem',
            color: isActive ? colors.primary.teal : colors.secondary.mediumGray,
            cursor: 'pointer',
            transition: 'all 150ms',
            fontWeight: isActive ? '600' : '400',
            fontFamily: '"Noto Sans", sans-serif'
          }}
        >
          <Icon size={14} />
          {label}
        </button>
      );
    }

    return null;
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
        padding: '1rem 1.5rem',
        backgroundColor: colors.text.white,
        borderRadius: '0.75rem',
        border: `1px solid ${colors.secondary.lightTeal}`,
        marginBottom
      }}
    >
      {/* Label "Filtrer par" */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Filter size={20} color={colors.primary.teal} />
        <span
          style={{
            fontFamily: '"Noto Sans", sans-serif',
            fontSize: '0.9375rem',
            fontWeight: '500',
            color: colors.primary.gray
          }}
        >
          Filtrer par :
        </span>
      </div>

      {/* Render all filters */}
      {filters.map((filter, index) => renderFilter(filter, index))}

      {/* Info display (ex: nombre de consultants) */}
      {infoDisplay}

      {/* Result count */}
      {resultCount && (
        <div
          style={{
            marginLeft: 'auto',
            fontSize: '0.8125rem',
            color: colors.secondary.mediumGray
          }}
        >
          {resultCount}
        </div>
      )}

      {/* Action buttons */}
      {actionButtons && (
        <div style={{ display: 'flex', gap: '0.75rem', ...(!resultCount && { marginLeft: 'auto' }) }}>
          {actionButtons}
        </div>
      )}
    </div>
  );
}

export default FilterBar;
